import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import authService from "../services/auth.service";
import { AUTH_UNAUTHORIZED_EVENT } from "../services/api.service";
import { toast } from "sonner";

interface User {
  email: string;
  name: string;
  role?: string;
  gender?: string;
  hasBookingAccess?: boolean;
  bookingAccessGrantedAt?: string;
  hasActiveBooking?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, password: string, name: string, phoneNumber: string, gender: string, role: string, studentId: string, emergencyContactName: string, emergencyContactNumber: string) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore the session from the httpOnly auth cookie on mount. There is no
  // client-readable token to gate on, so always ask the server who we are; a
  // 401 simply means "not logged in".
  useEffect(() => {
    const initAuth = async () => {
      try {
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        // No valid session cookie — remain logged out (no error toast).
        setUser(null);
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // If any request comes back 401 mid-session (cookie expired/invalid), drop the
  // stale user so the UI reflects the logged-out state.
  useEffect(() => {
    const handleUnauthorized = () => setUser(null);
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });
      const userData = response.data.user;
      setUser(userData);
      // The login response omits hasActiveBooking; hydrate the full profile so
      // booking-gated nav items resolve correctly without a manual refresh.
      await refreshUser();
      toast.success("Login successful!");
      return { success: true };
    } catch (error: any) {
      const errorMessage = error.message || "Login failed. Please check your credentials.";
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const signup = async (email: string, password: string, name: string, phoneNumber: string, gender: string, role: string, studentId: string, emergencyContactName: string, emergencyContactNumber: string) => {
    try {
      const response = await authService.signup({ email, password, name, phoneNumber, gender, role, studentId, emergencyContactName, emergencyContactNumber });

      // Check if user needs email verification
      if (response.data.requiresVerification) {
        // DO NOT log them in - they need to verify email first
        setUser(null);
        toast.success("Account created successfully! Please check your email to verify your account.");
        return true;
      }

      // Only log them in if verification is not required (server would have set
      // the auth cookie in that case).
      if (response.data.user) {
        setUser(response.data.user);
        toast.success("Account created successfully!");
      } else {
        setUser(null);
        toast.success("Account created successfully! Please check your email to verify your account.");
      }
      return true;
    } catch (error: any) {
      toast.error(error.message || "Signup failed. Please try again.");
      return false;
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    toast.success("Logged out successfully");
  };

  const refreshUser = async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch (error) {
      // Failed to refresh user
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
